import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MessageType } from '../../models/enums';
import { CommonService } from '../../services/common/common.service';
import { Subscription } from 'rxjs';

@Component({
    standalone: false,
    selector: 'res-custom-combo-box',
    templateUrl: './custom-combo-box.component.html',
    styleUrls: ['./custom-combo-box.component.scss']
})
export class CustomComboBoxComponent {
    @Input() items: any;

    private _selectedItem: any;
    get selectedItem(): any {
        return this._selectedItem;
    }
    @Input()
    set selectedItem(item: any) {
        this._selectedItem = item;
        if (this._selectedItem && this.value) {
            this._inputValue = this._selectedItem[this.value];
        } else {
            this._inputValue = this._selectedItem;
        }
    }
    @Output() selectedItemChange = new EventEmitter<any>();
    @Input() disabled: boolean;
    @Input() inputWidth: number;
    @Input() fillWidth: boolean;
    @Input() isFilterComboBox = false;
    @Input() onSelectionChanged: (item: any, itemClick: boolean) => void;
    @Input() onInputFocusLost: (item: any) => void;
    @Input() onInputFocus: (item: any) => void;
    // set this if the items are a list of objects, this should be the prop name that should be displayed in the combo box
    @Input() value: string;

    @Input() placeholder = 'Select or enter a value ...';
    @Input() automationId: string;
    @Input() maxlength: number;
    @Input() allItems: any;
    listButton: boolean;
    @Input() isForceOpen: boolean;
    @Input() isMouseOver: boolean;
    @Input() isListScrolled: boolean;
    @Input() paddingRight!: number;
    id: string;
    private outsideScrollClick: Subscription;

    private _inputValue: string;
    get inputValue(): string {
        return this._inputValue;
    }
    set inputValue(val: string) {

        this.showDropdown();

        this._inputValue = val;
        if (this.value) {
            this._selectedItem[this.value] = val;
        } else {
            this._selectedItem = val;
        }
        if (this.onSelectionChanged) {
            this.onSelectionChanged(this.selectedItem, false);
        }
        this.selectedItemChange.emit(this.selectedItem);
    }

    constructor(private commonService: CommonService) {
        this.id = 'dropdownMenu_' + window.crypto.getRandomValues(new Uint32Array(1))[0].toString(36);
        this.outsideScrollClick = this.commonService.getMessage(MessageType.outsideScrollClick).subscribe((data) => {
            this.listFocusOut();
        });
    }

    selectItem(item: any): void {
        this.selectedItem = item;
        if (this.onSelectionChanged) {
            this.onSelectionChanged(this.selectedItem, true);
            this.commonService.sendMessage(MessageType.fieldInFocus, false);
        }
        this.selectedItemChange.emit(this.selectedItem);
        this.hideDropdown();
    }

    onFocusLost = (event?: any) => {
        if (this.onInputFocusLost) {
            setTimeout(() => {
                this.selectedItem = this.selectedItem?.trim();
                this.onInputFocusLost(this.selectedItem);
                this.commonService.sendMessage(MessageType.fieldInFocus, false);
            }, 200);
        } else {
            this.commonService.sendMessage(MessageType.fieldInFocus, false);
        }
        if (this.isDropdownOpen() && !this.isMouseOver) {
            this.hideDropdown();
        }
    }

    tabOut = (event: any, button?: string) => {
        if (event?.key === 'Tab' || event && button === 'listAllButton') {
            if (this.listButton && button) {
                event.stopPropagation();
                this.listButton = false;
            }

            if (event?.currentTarget?.previousSibling !== event?.relatedTarget) {
                if (this.isDropdownOpen() && event?.key !== 'Tab' && !this.isMouseOver) {
                    this.hideDropdown();
                } else {
                    setTimeout(() => {
                        if (event?.target?.nodeName === 'BUTTON') {
                            this.getAllItems(event);
                            if (this.onSelectionChanged) {
                                this.onSelectionChanged(this.selectedItem, false);
                            }
                        } else {
                            this.filterData();
                        }

                    });
                }
            }
        } else if (event?.code === 'Backspace' && this.inputValue === '') {
            this.showDropdown(true, event);
            this.isForceOpen = true;
        }
    }

    onFocus = () => {
        this.commonService.sendMessage(MessageType.fieldInFocus, true);
        if (this.onInputFocus) {
            setTimeout(() => {
                this.onInputFocus(this.selectedItem);
            }, 200);
        }

        this.showDropdown();
    }

    onClick(event): void {
        this.filterData();
        this.hideDropdown(null, event);

        if (this.isForceOpen && !this.isDropdownOpen()) {
            this.hideDropdown();
            this.isForceOpen = false;
        }
    }

    filterData(): void {
        if (this.inputValue) {
            this.items = this.allItems?.filter(value => {
                return value?.toLowerCase()?.trim().includes(this.inputValue?.toLowerCase()?.trim());
            });
        } else {
            this.items = this.allItems;
        }
    }

    getAllItems(event): void {
        if (this.items !== this.allItems) {
            this.hideDropdown(null, event);
        }
        if (this.allItems) {
            this.listButton = true;
            this.items = this.allItems;
        }
    }

    mouseEnter(event): void {
        this.isMouseOver = true;
    }
    mouseLeave(event): void {
        this.isMouseOver = false;
    }

    scroll(event): void {
        this.isListScrolled = true;
    }

    listFocusOut = (event?: any) => {
        if (this.isListScrolled) {
            this.hideDropdown();
            this.isListScrolled = false;
        }
    }

    isDropdownOpen(): boolean {
        const dropdown = document.getElementById("dropdownMenu_" + this.automationId);
        return dropdown && dropdown.className === 'dropdown-menu show';
    }

    showDropdown(listButton?: boolean, event?: Event): void {
        const dropdown = document.getElementById("dropdownMenu_" + this.automationId);
        if (!this.commonService.comboboxDropdownHidden && dropdown && dropdown.className === 'dropdown-menu') {
            dropdown.classList.add('show');
            if (listButton && event) {
                this.listButton = true;
                event.stopPropagation();
            }
        }
    }

    hideDropdown(setForceOpen?: boolean, event?: Event): void {
        const dropdown = document.getElementById("dropdownMenu_" + this.automationId);
        if (dropdown && dropdown.className === 'dropdown-menu show') {
            if (event) {
                event.stopPropagation();
            } else {
                dropdown.classList.remove('show');
            }
        }
    }

    ngOnDestroy(): void {
        if (this.outsideScrollClick) {
            this.outsideScrollClick.unsubscribe();
        }
    }
}
